<template>
	<div>
		<TemplateModelSelectDialog
			v-if="!mainData"
			:name="excel_template.name"
			:itemData="items"
			:pagination="pagination"
			@set-data="setData($event)"
			@fetch-items="fetchItems(false, $event)"
		/>
		<template v-else>
			<v-row no-gutters class="pa-2" justify="end">
				<v-btn color="secondary" @click="exportExcel">
					<v-icon class="mr-2">mdi-export-variant</v-icon>
					エクセル出力</v-btn
				>
				<v-btn
					class="ml-2"
					color="secondary"
					@click="$router.push({ name: 'excel_template_edit', params: { id } })"
				>
					<v-icon class="mr-2">mdi-pencil</v-icon>
					編集画面へ
				</v-btn>
			</v-row>
			<v-tabs prev-icon="" next-icon="" center-active height="36">
				<template v-if="sheets.length">
					<v-tab v-for="sheet in sheets" :key="sheet.id" @click="setSheet(sheet.name)">{{
						sheet.name
					}}</v-tab>
				</template>
			</v-tabs>
			<v-row no-gutters justify="center">
				<v-progress-circular v-if="loading" :size="70" :width="7" color="primary" indeterminate />
			</v-row>
			<v-sheet class="table-wrapper" v-show="!loading">
				<div
					v-if="rows.length && columns.length"
					class="grid-container"
					:style="gridContainerWidth"
				>
					<template v-for="(row, i) in rows">
						<template v-if="headers.length == row.cells.length">
							<template v-for="(cell, j) in row.cells">
								<ExcelTemplateCell
									:ref="`cell${i}-${j}`"
									v-if="cellExists(cell)"
									:key="`${i}-${j}`"
									:cell="cell"
									:hidden="row.hidden == true || columns[j].hidden == true"
									:coordinate="{ yCoordinate: i, xCoordinate: j }"
								/>
							</template>
						</template>
						<template v-else>
							<template v-for="(header, j) in headers">
								<ExcelTemplateCell
									:ref="`cell${i}-${j}`"
									v-if="cellExists(cellData(row.cells, header))"
									:key="`${i}-${j}`"
									:cell="cellData(row.cells, header)"
									:hidden="row.hidden == true || columns[j].hidden == true"
									:coordinate="{ yCoordinate: i, xCoordinate: j }"
								/>
							</template>
						</template>
					</template>
				</div>
			</v-sheet>
		</template>
	</div>
</template>

<script>
import axios from "axios"
import moment from "moment/min/moment.min.js"
import * as Excel from "exceljs"
import errorHandling from "../../helper/errorHandling"
import authenticate from "../../helper/authenticate"
import excelHelper from "../../helper/excelHelper"
import ExcelTemplateCell from "./components/ExcelTemplateCell.vue"
import TemplateModelSelectDialog from "./components/TemplateModelSelectDialog.vue"

export default {
	components: {
		ExcelTemplateCell,
		TemplateModelSelectDialog,
	},
	data() {
		return {
			id: this.$route.params.id,
			items: [],
			excel_template: {
				name: null,
				base_model: null,
				exclusive_flag: false,
				department_id: null,
				file: null,
			},
			mainData: null,
			selectedSheetId: null,
			// authorized: null,
			coordinate: null,

			loading: false,

			workbook: null,
			headers: [],
			cells: [],
			sheets: [],
			columns: [],
			rows: [],
			mergedCells: [],

			formula: {
				addition: "+",
				subtraction: "-",
				multiplication: "*",
				division: "/",
			},

			pagination: {
				count: null,
				current: 1,
				limit_value: null,
				next: null,
				pages: null,
				previous: null,
			},
		}
	},
	mounted() {
		authenticate.authenticateUser()
		this.fetchItems(true, 1)
	},
	methods: {
		fetchItems(first_visit, page) {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/excel_templates/${this.id}/`
			axios
				.get(url, { params: { page, first_visit }, headers })
				.then((res) => {
					this.items = res.data.items
					this.pagination = res.data.pagination
					if (first_visit) {
						this.excel_template = res.data.excel_template
						this.cells = res.data.cells
						this.excel_file = res.data.excel_file
						// this.authorized = res.data.authorized
					}
				})
				.catch((err) => {
					this.loading = false
					errorHandling.errorResponse(err)
				})
		},
		exportExcel() {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/excel_templates/${this.id}/excels/${this.mainData.id}`
			axios
				.get(url, { headers, responseType: "blob" })
				.then((res) => {
					var FILE = window.URL.createObjectURL(new Blob([res.data]), {
						type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
					})
					var docUrl = document.createElement("a")
					docUrl.href = FILE
					docUrl.setAttribute("download", `${this.excel_file.name}`)
					document.body.appendChild(docUrl)
					docUrl.click()
					docUrl.parentNode.removeChild(docUrl)
				})
				.catch((err) => {
					this.loading = false
					errorHandling.errorResponse(err)
				})
		},
		setData(data) {
			this.mainData = data
			this.readExcelData()
		},
		setAllCellData() {
			const cells = this.cells.filter((c) => c.sheet_number == this.selectedSheetId)
			cells.forEach((cell) => {
				this.setCellData(cell)
			})
			this.$nextTick(() => {
				this.loading = false
			})
		},
		setCellData(cell) {
			const data = this.mainData.data[cell.id]
			if (!data) return
			if (cell.array_direction) {
				const maxCount = cell.max_count || 10
				let skipped_cell_count = 0
				for (let i = 0; i < maxCount; i++) {
					let value = this.cellValue(data[i], cell)
					skipped_cell_count += this.skip_cells(cell, skipped_cell_count + i)
					if (cell.array_direction == "x_axis") {
						this.appendCellData(
							value,
							cell.y_coordinate,
							cell.x_coordinate + skipped_cell_count + i
						)
					} else if (cell.array_direction == "y_axis") {
						this.appendCellData(
							value,
							cell.y_coordinate + skipped_cell_count + i,
							cell.x_coordinate
						)
					}
				}
			} else {
				const value = this.cellValue(data, cell)
				this.appendCellData(value, cell.y_coordinate, cell.x_coordinate)
			}
		},
		skip_cells(cell, already_skipped_count) {
			let foundNextCell = false
			let skip_cells_count = 0
			let xCoordinate =
				cell.array_direction == "x_axis"
					? cell.x_coordinate + already_skipped_count
					: cell.x_coordinate
			let yCoordinate =
				cell.array_direction == "y_axis"
					? cell.y_coordinate + already_skipped_count
					: cell.y_coordinate

			while (foundNextCell == false) {
				// セルがこれ以上存在しない
				if (this.columns.length < yCoordinate + 1 || this.rows.length < xCoordinate + 1) {
					foundNextCell = true
					// セルが隠れている
				} else if (
					this.columns[xCoordinate]?.hidden == true ||
					this.rows[yCoordinate]?.hidden == true
				) {
					if (cell.array_direction == "x_axis") {
						xCoordinate += 1
					} else if (cell.array_direction == "y_axis") {
						yCoordinate += 1
					}
					skip_cells_count += 1
				} else {
					// セルがない、またはマージされている
					let row = this.rows[yCoordinate]
					let targetCell =
						this.headers.length == row.cells.length
							? row.cells[xCoordinate]
							: this.cellData(row.cells, this.headers[yCoordinate])
					if (!targetCell || !targetCell.master) {
						foundNextCell = true
					} else {
						if (cell.array_direction == "x_axis") {
							xCoordinate += 1
						} else if (cell.array_direction == "y_axis") {
							yCoordinate += 1
						}
						skip_cells_count += 1
					}
				}
			}
			return skip_cells_count
		},
		removeCellData(cell) {
			if (cell.array_direction) {
				const maxCount = cell.max_count || 10
				let skipped_cell_count = 0
				for (let i = 0; i < maxCount; i++) {
					if (cell.array_direction == "x_axis") {
						skipped_cell_count += this.skip_cells(cell, skipped_cell_count + i)
						this.appendCellData(null, cell.y_coordinate, cell.x_coordinate + skipped_cell_count + i)
					} else if (cell.array_direction == "y_axis") {
						skipped_cell_count += this.skip_cells(cell, skipped_cell_count + i)
						this.appendCellData(null, cell.y_coordinate + skipped_cell_count + i, cell.x_coordinate)
					}
				}
			} else {
				this.appendCellData(null, cell.y_coordinate, cell.x_coordinate)
			}
		},
		cellValue(value, cell) {
			if (value == null) return ""
			if (cell.prefix_formula && cell.prefix_number != null) {
				return Function(
					"return (" + `${cell.prefix_number}${this.formula[cell.prefix_formula]}${value}` + ");"
				)()
			} else if (cell.suffix_formula && cell.suffix_number != null) {
				return Function(
					"return (" + `${value}${this.formula[cell.suffix_formula]}${cell.suffix_number}` + ");"
				)()
			} else {
				if (cell.options.length) {
					const option = cell.options.find((o) => o.id == value)
					if (option) return option.name
				}
				return this.formatValue(value, cell)
			}
		},
		formatValue(value, cell) {
			if (value == null) return ""
			switch (cell.column_type) {
				case "string":
				case "text":
				case "integer":
				case "float":
					return value
				case "date":
					return moment(value).format("YYYY/MM/DD")
				case "datetime":
					return moment(value).format("YYYY/MM/DD HH:mm")
				case "boolean":
					if (value == true) {
						return "○"
					} else if (value == false) {
						return "x"
					}
			}
		},
		readExcelData() {
			axios
				.get(this.excel_file.url, {
					responseType: "blob",
				})
				.then(async (res) => {
					const file = new File([res.data], "file1.xlsx", {
						type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
					})
					this.workbook = new Excel.Workbook()
					this.workbook.xlsx
						.load(await file.arrayBuffer())
						.then((res) => {
							this.setExcelData()
						})
						.catch((err) => {
							this.loading = false
							errorHandling.errorResponse(err)
						})
				})
				.catch((err) => {
					this.loading = false
					errorHandling.errorResponse(err)
				})
		},
		setExcelData() {
			this.workbook.eachSheet((worksheet, sheetId) => {
				this.sheets.push({
					name: worksheet.name,
					id: sheetId,
				})
			})
			this.setSheet(this.sheets[0]?.name)
		},
		setSheet(name) {
			this.loading = true
			if (this.selectedSheetId) {
				this.resetCellData()
				this.resetMergeCells()
			}
			const sheet = this.workbook.getWorksheet(name)
			this.selectedSheetId = sheet.id
			this.mergedCells = []
			this.columns = []
			this.rows = []
			this.mergedCells = sheet.model.merges
			const excelWithModifyNumber = 8.6
			for (let i = 1; i <= sheet.columnCount; i++) {
				const col = sheet.getColumn(i)
				const width = col.width * excelWithModifyNumber
				this.columns.push({
					width: width,
					hidden: col.hidden,
				})
			}
			for (let i = 1; i <= sheet.rowCount; i++) {
				const row = sheet.getRow(i)
				const height = excelHelper.dpiToPixel(row.height)
				this.rows.push({
					height: height,
					hidden: row.hidden,
					cells: row.model ? row.model.cells : [],
				})
			}
			this.headers = excelHelper.getHeaders(sheet.columnCount)
			this.$nextTick(() => {
				this.setAllCellData()
				this.mergeCells()
			})
		},
		resetCellData() {
			const cells = this.cells.filter((c) => c.sheet_number == this.selectedSheetId)
			cells.forEach((cell) => {
				this.removeCellData(cell)
			})
		},
		appendCellData(name, y, x) {
			try {
				this.$refs[`cell${y}-${x}`][0].cellData = name
			} catch (err) {
				if (name) {
					this.$store.dispatch("setFlash", {
						type: "alert",
						text: "適当するセルが見つかりませんでした。",
					})
				}
			}
		},
		cellData(cells, header) {
			return cells.find((cell) => cell.address.match(/^[A-Z]+/)[0] == header)
		},
		mergeCells() {
			this.mergedCells.forEach((c) => {
				const mergeInfo = excelHelper.mergedCellInfo(c)
				if (mergeInfo.right - mergeInfo.left > 0) {
					this.$refs[`cell${mergeInfo.top - 1}-${mergeInfo.left - 1}`][0].mergeXAxis =
						mergeInfo.right - mergeInfo.left + 1
					this.$refs[`cell${mergeInfo.top - 1}-${mergeInfo.left - 1}`][0].columns = this.columns
				}
				if (mergeInfo.bottom - mergeInfo.top > 0) {
					this.$refs[`cell${mergeInfo.top - 1}-${mergeInfo.left - 1}`][0].mergeYAxis =
						mergeInfo.bottom - mergeInfo.top + 1
				}
			})
		},
		resetMergeCells() {
			this.mergedCells.forEach((c) => {
				const mergeInfo = excelHelper.mergedCellInfo(c)
				if (mergeInfo.right - mergeInfo.left > 0) {
					this.$refs[`cell${mergeInfo.top - 1}-${mergeInfo.left - 1}`][0].mergeXAxis = null
					mergeInfo.right - mergeInfo.left + 1
					this.$refs[`cell${mergeInfo.top - 1}-${mergeInfo.left - 1}`][0].columns = []
				}
				if (mergeInfo.bottom - mergeInfo.top > 0) {
					this.$refs[`cell${mergeInfo.top - 1}-${mergeInfo.left - 1}`][0].mergeYAxis = null
					mergeInfo.bottom - mergeInfo.top + 1
				}
			})
		},
		recordStyle(row) {
			return { height: `${row.height}px` }
		},
		cellExists(cell) {
			return !cell || !cell.master
		},
	},
	computed: {
		gridContainerWidth() {
			let style = {}
			const columns = []
			const defaultCellSize = 104
			let width = this.columns.reduce((preVal, currentVal, i) => {
				if (this.columns[i].hidden == true) {
					columns.push(0 + "px")
					return preVal
				} else {
					columns.push(currentVal.width ? currentVal.width + "px" : defaultCellSize + "px")
					return preVal + (currentVal.width || defaultCellSize)
				}
			}, 0)
			style.width = width + "px"
			style.gridTemplateColumns = columns.join(" ")
			return style
		},
	},
}
</script>

<style lang="scss" scoped>
.table-wrapper {
	padding: 1rem;
	overflow: scroll;
	max-width: 100vw;
}
.grid-container {
	display: grid;
	border: thin solid grey;
	justify-items: stretch;
	align-content: stretch;
	div {
		padding: 4px;
		font-size: 0.85rem;
		min-height: 20px;
		border: 0.5px solid grey;
		white-space: pre-wrap;
		word-break: break-all;
	}
	.hidden-cell {
		padding: 0px;
		border: none;
		min-height: 0px;
		max-height: 0px;
		min-width: 0px;
		max-width: 0px;
		overflow: hidden;
	}
}
</style>